import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { toast } from 'react-toastify';
import { 
  Plus, Search, Edit2, Trash2, Users,
  Building2, UserCheck, Calendar, User,
  Mail, Phone, Briefcase, MapPin, ToggleLeft, ToggleRight, ChevronLeft, ChevronRight
} from 'lucide-react';
import api from '../components/api';
import Modal from '../components/Modal';
import FormInput from '../components/FormInput';
import { useNavigate } from 'react-router-dom';
import { getDeleteConfirmation } from '../utils/confirmationUtils';
import ConfirmationModal from '../components/ConfirmationModal';

const PersonnelPage = () => {
  const navigate = useNavigate();
  const [personnel, setPersonnel] = useState([]);
  const [departements, setDepartements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    matricule: '',
    nom: '',
    prenom: '',
    date_naissance: '',
    lieu_naissance: '',
    genre: '',
    email: '',
    telephone: '',
    adresse: '',
    type_contrat: '',
    role: '',
    departement: '',
    date_embauche: '',
    date_fin_contrat: '',
    photo: null
  });
  const [formErrors, setFormErrors] = useState({});
  
  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [personnelToDelete, setPersonnelToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    current: 1,
    total_pages: 1
  });

  useEffect(() => {
    fetchPersonnels();
  }, []);

  const fetchPersonnels = async (page = 1) => {
    try {
      setLoading(true);
      const [personnelsResponse, departementsResponse] = await Promise.all([
        api.get('/etablissement/personnels/', {
          params: {
            page: page,
            page_size: 10
          }
        }),
        api.get('/etablissement/departements/')
      ]);
      
      setPersonnel(personnelsResponse.data.results);
      // Extraire les résultats de la réponse paginée des départements
      const departementsData = departementsResponse.data.results || departementsResponse.data;
      setDepartements(departementsData);
      setPagination({
        count: personnelsResponse.data.count,
        next: personnelsResponse.data.next,
        previous: personnelsResponse.data.previous,
        current: page,
        total_pages: Math.ceil(personnelsResponse.data.count / 10)
      });
      setLoading(false);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      setLoading(false);
    }
  };

  const fetchPersonnelDetails = async (id) => {
    try {
      const response = await api.get(`/etablissement/personnels/${id}/`);
      return response.data;
    } catch (error) {
      toast.error('Erreur lors de la récupération des détails du personnel');
      return null;
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: undefined
      });
    }
  };

  const handleOpenModal = async (member = null) => {
    if (member) {
      // Récupérer les détails complets du personnel
      const details = await fetchPersonnelDetails(member.id);
      if (!details) return;

      setFormData({
        matricule: details.matricule || '',
        nom: details.nom || '',
        prenom: details.prenom || '',
        date_naissance: details.date_naissance || '',
        lieu_naissance: details.lieu_naissance || '',
        genre: details.genre || '',
        email: details.email || '',
        telephone: details.telephone || '',
        adresse: details.adresse || '',
        type_contrat: details.type_contrat || '',
        role: details.role || '',
        departement: details.departement?.id || '',
        date_embauche: details.date_embauche || '',
        date_fin_contrat: details.date_fin_contrat || '',
        photo: null
      });
      setSelectedPersonnel(details);
    } else {
      setFormData({
        matricule: '',
        nom: '',
        prenom: '',
        date_naissance: '',
        lieu_naissance: '',
        genre: '',
        email: '',
        telephone: '',
        adresse: '',
        type_contrat: '',
        role: '',
        departement: '',
        date_embauche: '',
        date_fin_contrat: '',
        photo: null
      });
      setSelectedPersonnel(null);
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.matricule.trim()) errors.matricule = 'Le matricule est requis';
    if (!formData.nom.trim()) errors.nom = 'Le nom est requis';
    if (!formData.prenom.trim()) errors.prenom = 'Le prénom est requis';
    if (!formData.date_naissance) errors.date_naissance = 'La date de naissance est requise';
    if (!formData.lieu_naissance.trim()) errors.lieu_naissance = 'Le lieu de naissance est requis';
    if (!formData.genre) errors.genre = 'Le genre est requis';
    if (!formData.email.trim()) errors.email = 'L\'email est requis';
    if (!formData.telephone.trim()) errors.telephone = 'Le téléphone est requis';
    if (!formData.type_contrat) errors.type_contrat = 'Le type de contrat est requis';
    if (!formData.role) errors.role = 'Le rôle est requis';
    if (!formData.date_embauche) errors.date_embauche = 'La date d\'embauche est requise';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null) {
          formDataToSend.append(key, formData[key]);
        }
      });

      if (selectedPersonnel) {
        await api.put(`/etablissement/personnels/${selectedPersonnel.id}/`, formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Membre du personnel modifié avec succès');
      } else {
        await api.post('/etablissement/personnels/', formDataToSend, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        toast.success('Membre du personnel créé avec succès');
      }
      setIsModalOpen(false);
      fetchPersonnels();
    } catch (error) {
      if (error.response?.data) {
        const apiErrors = {};
        Object.entries(error.response.data).forEach(([field, messages]) => {
          apiErrors[field] = Array.isArray(messages) ? messages[0] : messages;
        });
        setFormErrors(apiErrors);
      } else {
        toast.error('Erreur lors de la sauvegarde du membre du personnel');
      }
    }
  };

  const handleOpenDeleteModal = (member) => {
    setPersonnelToDelete(member);
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!personnelToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/etablissement/personnels/${personnelToDelete.id}/`);
      toast.success('Membre du personnel supprimé avec succès');
      fetchPersonnels();
      setIsConfirmModalOpen(false);
      setPersonnelToDelete(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression du membre du personnel');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleToggleStatus = async (member) => {
    try {
      await api.post(`/etablissement/personnels/${member.id}/toggle-status/`);
      fetchPersonnels();
      toast.success(`${member.nom} ${member.prenom} a été ${member.is_active ? 'désactivé' : 'activé'}`);
    } catch (error) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const confirmConfig = personnelToDelete 
    ? getDeleteConfirmation(personnelToDelete, 'le membre du personnel', 'nom_complet', 'supprimer') 
    : {};

  const filteredPersonnel = personnel.filter(member =>
    member.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.matricule.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.departement_nom || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handlePageChange = (newPage) => {
    fetchPersonnels(newPage);
  };

  // Composant de pagination
  const Pagination = () => (
    <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => handlePageChange(pagination.current - 1)}
          disabled={!pagination.previous}
          className={`relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 ${
            !pagination.previous 
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-gray-50'
          }`}
        >
          Précédent
        </button>
        <button
          onClick={() => handlePageChange(pagination.current + 1)}
          disabled={!pagination.next}
          className={`relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 ${
            !pagination.next 
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-gray-50'
          }`}
        >
          Suivant
        </button>
      </div>
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-700">
            Affichage de{' '}
            <span className="font-medium">
              {((pagination.current - 1) * 10) + 1}
            </span>{' '}
            à{' '}
            <span className="font-medium">
              {Math.min(pagination.current * 10, pagination.count)}
            </span>{' '}
            sur{' '}
            <span className="font-medium">{pagination.count}</span> membres du personnel
          </p>
        </div>
        <div>
          <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              onClick={() => handlePageChange(pagination.current - 1)}
              disabled={!pagination.previous}
              className={`relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                !pagination.previous 
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Précédent</span>
              <ChevronLeft className="h-5 w-5" aria-hidden="true" />
            </button>
            {[...Array(pagination.total_pages)].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => handlePageChange(i + 1)}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                  pagination.current === i + 1
                    ? 'z-10 bg-[#202654] text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#202654]'
                    : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(pagination.current + 1)}
              disabled={!pagination.next}
              className={`relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 ${
                !pagination.next 
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50'
              }`}
            >
              <span className="sr-only">Suivant</span>
              <ChevronRight className="h-5 w-5" aria-hidden="true" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );

  const renderModalFooter = () => (
    <div className="flex justify-end space-x-3">
      <button
        type="button"
        onClick={() => setIsModalOpen(false)}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        Annuler
      </button>
      <button
        type="submit"
        onClick={handleSubmit}
        className="px-4 py-2 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
      >
        {selectedPersonnel ? 'Modifier' : 'Créer'}
      </button>
    </div>
  );

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        {/* Header avec bannière */}
        <div className="bg-gradient-to-r from-[#202654] to-[#3a4999] rounded-xl p-6 mb-8 shadow-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center text-white">
              <button
                onClick={() => navigate(-1)}
                className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold">Gestion du Personnel</h1>
                <p className="text-white opacity-80 mt-1">
                  Gérez les membres du personnel et leurs affectations
                </p>
              </div>
            </div>
            <div className="flex gap-4 w-full md:w-auto">
              <div className="relative flex-grow md:flex-grow-0">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un membre..."
                  className="pl-10 pr-4 py-2 w-full md:w-64 bg-white/90 backdrop-blur-sm text-gray-900 rounded-lg"
                />
              </div>
              <button
                onClick={() => handleOpenModal()}
                className="inline-flex items-center bg-white text-[#202654] px-4 py-2 rounded-lg shadow-md hover:bg-gray-100 transition-all duration-200 whitespace-nowrap"
              >
                <Plus className="h-5 w-5 mr-2" />
                Nouveau Membre
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center p-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#202654]"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Membre
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Département
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rôle
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPersonnel.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12">
                        <div className="flex flex-col items-center justify-center text-center">
                          <Users className="h-12 w-12 text-gray-400 mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-1">Aucun membre trouvé</h3>
                          <p className="text-sm text-gray-500 max-w-md">
                            {searchTerm ? 'Essayez avec un autre terme de recherche' : 'Commencez par ajouter votre premier membre'}
                          </p>
                          {!searchTerm && (
                            <button
                              onClick={() => handleOpenModal()}
                              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90"
                            >
                              <Plus className="h-5 w-5 mr-2" />
                              Ajouter un membre
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredPersonnel.map((member) => (
                      <tr key={member.id} className={`border-b ${!member.is_active ? 'bg-gray-100' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              {member.photo ? (
                                <img
                                  className="h-10 w-10 rounded-full object-cover"
                                  src={member.photo}
                                  alt={`${member.nom} ${member.prenom}`}
                                />
                              ) : (
                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                  <UserCheck className="h-6 w-6 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {member.nom} {member.prenom}
                              </div>
                              <div className="text-sm text-gray-500">
                                {member.matricule}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Mail className="h-4 w-4 text-gray-400 mr-1" />
                            {member.email}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center mt-1">
                            <Phone className="h-4 w-4 text-gray-400 mr-1" />
                            {member.telephone}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {member.departement_nom ? (
                            <div className="flex items-center">
                              <Building2 className="h-5 w-5 text-gray-400 mr-2" />
                              <span className="text-sm text-gray-900">{member.departement_nom}</span>
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              Non affecté
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Briefcase className="h-5 w-5 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-900">{member.role}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            member.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {member.is_active ? 'Actif' : 'Inactif'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => handleToggleStatus(member)}
                              className={`text-${member.is_active ? 'red' : 'green'}-600 hover:text-${member.is_active ? 'red' : 'green'}-900`}
                              title={member.is_active ? 'Désactiver' : 'Activer'}
                            >
                              {member.is_active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                            </button>
                            <button
                              onClick={() => handleOpenModal(member)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Modifier"
                            >
                              <Edit2 className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleOpenDeleteModal(member)}
                              className="text-red-600 hover:text-red-900"
                              title="Supprimer"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
          {!loading && <Pagination />}
        </div>
      </div>

      {/* Modal pour création/édition de membre */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedPersonnel ? 'Modifier le membre' : 'Nouveau membre'}
        footer={renderModalFooter()}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput
              label="Matricule"
              type="text"
              name="matricule"
              value={formData.matricule}
              onChange={handleInputChange}
              placeholder="Ex: EMP001"
              required
              error={formErrors.matricule}
              icon={User}
            />
            
            <FormInput
              label="Nom"
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              placeholder="Nom de famille"
              required
              error={formErrors.nom}
            />
            
            <FormInput
              label="Prénom"
              type="text"
              name="prenom"
              value={formData.prenom}
              onChange={handleInputChange}
              placeholder="Prénom(s)"
              required
              error={formErrors.prenom}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput
              label="Date de naissance"
              type="date"
              name="date_naissance"
              value={formData.date_naissance}
              onChange={handleInputChange}
              required
              error={formErrors.date_naissance}
              icon={Calendar}
            />
            
            <FormInput
              label="Lieu de naissance"
              type="text"
              name="lieu_naissance"
              value={formData.lieu_naissance}
              onChange={handleInputChange}
              placeholder="Ville de naissance"
              required
              error={formErrors.lieu_naissance}
              icon={MapPin}
            />
            
            <FormInput
              label="Genre"
              type="select"
              name="genre"
              value={formData.genre}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Sélectionner le genre' },
                { value: 'M', label: 'Masculin' },
                { value: 'F', label: 'Féminin' }
              ]}
              required
              error={formErrors.genre}
              icon={User}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput
              label="Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="adresse@email.com"
              required
              error={formErrors.email}
              icon={Mail}
            />
            
            <FormInput
              label="Téléphone"
              type="tel"
              name="telephone"
              value={formData.telephone}
              onChange={handleInputChange}
              placeholder="Numéro de téléphone"
              required
              error={formErrors.telephone}
              icon={Phone}
            />
            
            <FormInput
              label="Type de contrat"
              type="select"
              name="type_contrat"
              value={formData.type_contrat}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Sélectionner le type de contrat' },
                { value: 'CDI', label: 'CDI' },
                { value: 'CDD', label: 'CDD' },
                { value: 'VACATION', label: 'Vacation' }
              ]}
              required
              error={formErrors.type_contrat}
              icon={Briefcase}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput
              label="Rôle"
              type="select"
              name="role"
              value={formData.role}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Sélectionner le rôle' },
                { value: 'ADMIN', label: 'Administrateur' },
                { value: 'ENSEIGNANT', label: 'Enseignant' },
                { value: 'SECRETAIRE', label: 'Secrétaire' },
                { value: 'COMPTABLE', label: 'Comptable' },
                { value: 'AUTRE', label: 'Autre' }
              ]}
              required
              error={formErrors.role}
              icon={UserCheck}
            />
            
            <FormInput
              label="Département"
              type="select"
              name="departement"
              value={formData.departement}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Sélectionner le département' },
                ...departements.map(dept => ({
                  value: dept.id,
                  label: dept.libelle
                }))
              ]}
              error={formErrors.departement}
              icon={Building2}
            />
            
            <FormInput
              label="Photo"
              type="file"
              name="photo"
              onChange={handleInputChange}
              accept="image/*"
              error={formErrors.photo}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput
              label="Date d'embauche"
              type="date"
              name="date_embauche"
              value={formData.date_embauche}
              onChange={handleInputChange}
              required
              error={formErrors.date_embauche}
              icon={Calendar}
            />
            
            <FormInput
              label="Date de fin de contrat"
              type="date"
              name="date_fin_contrat"
              value={formData.date_fin_contrat}
              onChange={handleInputChange}
              error={formErrors.date_fin_contrat}
              icon={Calendar}
              helpText="Optionnel. Pour CDD et vacations."
            />
            
            <FormInput
              label="Adresse"
              type="text"
              name="adresse"
              value={formData.adresse}
              onChange={handleInputChange}
              placeholder="Adresse complète"
              required
              error={formErrors.adresse}
              icon={MapPin}
            />
          </div>
        </form>
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer le membre"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer ce membre ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </Layout>
  );
};

export default PersonnelPage;
